import classNames from "classnames";
import Image from "next/image";
import * as routes from "routes";
import { descriptionsByGame } from "shared/games/allTechniqueGameDescriptions";
import { Difficulty } from "shared/games/Difficulty";
import { Game } from "shared/games/types/Game";
import Shape from "ui/atoms/Shape/Shape";
import Link from "util/Link";

interface Props {
  difficulty: Difficulty;
  game: Game;
  countComplete: number;
  total: number;
  selected?: boolean;
}

export default function UpNextCard({
  difficulty,
  game,
  countComplete = 3,
  total = 5,
  selected = false,
}: Props) {
  const gameDesc = descriptionsByGame[game];
  const completed = countComplete >= total;

  return (
    <div
      className={classNames(
        "p-2 overflow-hidden rounded w-56 md:w-80 h-full flex flex-col",
        completed
          ? "bg-emerald-500 text-white"
          : {
              "bg-white shadow-2xl text-black": selected,
              "bg-gray-500 text-black": !selected,
            },
        selected && "animate-bounce"
      )}
    >
      {/* Title */}
      <div className={"font-brik leading-none text-xl whitespace-normal mb-2"}>
        {gameDesc.shortTitle}
      </div>

      {/* Image */}
      <div
        className={"overflow-hidden relative h-24 md:h-40 rounded shadow mb-2"}
      >
        <Image
          src={`/images/game-screenshots/${gameDesc.game}.png`}
          width={500}
          height={300}
          className="object-cover absolute left-0 top-0 scale-105"
          alt={gameDesc.title}
        />

        {completed && (
          <div
            className={
              "bg-emerald-500/80 inset-0 absolute font-brik flex items-center justify-center text-3xl whitespace-normal text-center"
            }
          >
            All Loops Finished For Now 🎉
          </div>
        )}
      </div>

      {/* Progress Bar */}
      <div className={"mb-4"}>
        <div className={"text-xs opacity-50 mb-1"}>
          {countComplete}/{total} Completed
        </div>

        <div
          className={classNames(
            "relative rounded w-full px-2 h-2 overflow-hidden",
            completed
              ? {
                  "bg-emerald-600": true,
                }
              : {
                  "bg-gray-500": selected,
                  "bg-gray-600": !selected,
                }
          )}
        >
          <div
            className={classNames(
              "bg-emerald-500 top-0 left-0 bottom-0 absolute shadow",
              completed ? "rounded" : "rounded-l"
            )}
            style={{
              right: `${((total - countComplete) / total) * 100}%`,
            }}
          />
        </div>
      </div>

      {/* Start Button */}
      {completed ? (
        <div />
      ) : (
        <div className={"flex justify-center mb-4"}>
          <div className={"max-w-[125px] w-full"}>
            <Shape
              theme={selected ? "blue" : "default"}
              disabled={completed}
              type="content"
              shape="circle"
              href={routes.earTraining.mixing.todosQueryParams({
                type: "todos",
                difficulty,
                games: [game],
                back: routes.earTraining.mixing.home,
              })}
              as={Link}
              outline={selected ? undefined : "thin"}
              onClick={() => {
                // TODO: ad tracking
              }}
            >
              <span
                className={classNames(
                  "font-brik",
                  selected ? "text-lg" : "text-sm"
                )}
              >
                Start
              </span>
            </Shape>
          </div>
        </div>
      )}
    </div>
  );
}
